import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Sparkles, MessageCircle, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { scrollToId, whatsappLink } from "@/lib/scroll";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/c6b1743b-aeeb-4789-b624-1b8eb6495fe9/images/97b61af1e11db36a9e2761b426d726f23b94b7037cf9f00eef80a7cc83b9eb3c.jpeg";

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const { t } = useTranslation();
  const STATS = t("hero.stats", { returnObjects: true });
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 120, damping: 16 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-6, 6]), { stiffness: 120, damping: 16 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <section id="inicio" data-testid="hero-section" className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-14 sm:pt-20 pb-16 sm:pb-24">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-10 items-center">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            data-testid="hero-badge"
            className="inline-flex items-center gap-2 rounded-full border border-[#F5CB5C]/30 bg-[#F5CB5C]/5 px-4 py-1.5 text-[11px] sm:text-xs tracking-wide text-[#F5CB5C]"
          >
            <Sparkles size={14} />
            {t("hero.badge")}
          </motion.div>

          <h1 data-testid="hero-headline" className="mt-6 font-display text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.12] tracking-tight">
            <Line delay={0.15}>{t("hero.line1")}</Line>
            <Line delay={0.27}>{t("hero.line2")}</Line>
            <Line delay={0.39}>
              {t("hero.line3")} <span className="text-gold-gradient">{t("hero.highlight")}</span>
            </Line>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.55 }}
            className="mt-6 text-base md:text-lg text-gray-300 max-w-xl"
          >
            {t("hero.sub1")}
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.65 }}
            className="mt-4 text-sm md:text-base text-gray-400 max-w-xl border-l-2 border-[#F5CB5C]/60 pl-4"
          >
            {t("hero.sub2")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75 }}
            className="mt-8 grid grid-cols-3 gap-4 max-w-lg"
          >
            {STATS.map((s) => (
              <div key={s.l} data-testid={`hero-stat-${s.l.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <div className="font-display text-xl sm:text-2xl text-gold-gradient whitespace-nowrap">{s.n}</div>
                <div className="text-[10px] sm:text-xs uppercase tracking-[0.14em] text-gray-500 mt-1">{s.l}</div>
              </div>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <button
              data-testid="hero-quote-button"
              onClick={() => scrollToId("#presupuesto")}
              className="inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 text-sm font-bold text-[#05070A] bg-gradient-to-br from-[#FAE19C] via-[#F5CB5C] to-[#C69222] hover:shadow-[0_0_32px_rgba(245,203,92,0.5)] hover:-translate-y-0.5 transition-all duration-300"
            >
              {t("hero.ctaQuote")} <ArrowRight size={16} />
            </button>
            <a
              data-testid="hero-whatsapp-button"
              href={whatsappLink(t("whatsapp.message"))}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 text-sm font-semibold text-gray-200 border border-gray-600 hover:border-[#25D366]/70 hover:text-white transition-all duration-300"
            >
              <MessageCircle size={17} className="text-[#25D366]" /> {t("hero.ctaWhatsapp")}
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-5"
          style={{ perspective: 1000 }}
        >
          <motion.div
            data-testid="hero-engraved-glass-card"
            onMouseMove={onMove}
            onMouseLeave={onLeave}
            style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
            className="glass-card glass-lift rounded-3xl overflow-hidden"
          >
            <div className="img-zoom relative">
              <img
                src={HERO_IMG}
                alt="Copa de cristal con grabado láser UV esmerilado"
                className="w-full h-[340px] sm:h-[420px] object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#05070A]/70 via-transparent to-transparent pointer-events-none" />
            </div>
            <div className="p-5 sm:p-6 flex items-start justify-between gap-4">
              <div>
                <div className="font-display text-white text-base">{t("hero.cardTitle")}</div>
                <p className="text-xs sm:text-sm text-gray-400 mt-1.5 leading-relaxed">
                  {t("hero.cardDesc")}
                </p>
              </div>
              <span data-testid="hero-finish-tag" className="shrink-0 rounded-full border border-[#F5CB5C]/40 bg-[#F5CB5C]/10 px-3 py-1.5 text-[9px] sm:text-[10px] tracking-[0.18em] text-[#F5CB5C] font-semibold">
                {t("hero.cardTag")}
              </span>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
